import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getPtEvalNoteForm,
  updatePtEvalNoteForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  DeleteIcon,
  FileCheckIcon,
  HomeIcon,
  MedicalNotesIcon,
  PlusIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  PT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  AMBULATION_OPTIONS,
  ASSISTIVE_DEVICE_OPTIONS,
  BALANCE_STATUS_OPTIONS,
  BARRIERS_OPTIONS,
  BED_MOBILITY_OPTIONS,
  ENTRANCE_OPTIONS,
  FALL_RISK_OPTIONS,
  GAIT_AID_OPTIONS,
  LIVES_WITH_OPTIONS,
  PT_EVAL_ARRAY_KEYS,
  PT_INTERVENTION_OPTIONS,
  REHAB_POTENTIAL_OPTIONS,
  RESIDENCE_TYPE_OPTIONS,
  SKIN_INTEGRITY_OPTIONS,
  STAIRS_OPTIONS,
  STRENGTH_ROM_JOINTS,
  TRANSFER_OPTIONS,
  YES_NO_OPTIONS,
  coerceExclusiveChoice,
  emptyPtEvalPayload,
  emptyStrengthRomRows,
  toggleInList,
} from "./ptEvalNoteContent";
import {
  emptyTextGoalRow,
  normalizeTextGoals,
  renumberTextGoals,
  serializeTextGoals,
} from "./ptGoals";
import PtNotesCommonHeader from "./PtNotesCommonHeader";
import PainScalePills from "./PainScalePills";
import {
  normalizePtCommonHeaderState,
  resolvePtNotesCommonHeader,
  withNormalizedPtCommonHeader,
} from "./ptNotesCommonHeaderUtils";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "./ptVisitCommon";

const EVAL_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyPtEvalPayload(),
  evalDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const SelectField = ({ value, options, canEdit, onChange, placeholder }) => (
  <select
    className={fieldClass}
    value={value || ""}
    disabled={!canEdit}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">{placeholder || "—"}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

SelectField.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

/** Red tint when MMT looks weak or ROM looks limited (simple clinical cue). */
const looksAbnormal = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return false;
  if (/^[0-2]([+/]-)?(\/5)?$/i.test(s) || /^3-?\/5$/i.test(s) || /^3\+\/5$/i.test(s))
    return true;
  if (/^-?\d+/.test(s) && Number.parseInt(s, 10) < 0) return true;
  return false;
};

const PtEvalNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyPtEvalPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const byJoint = Object.fromEntries(
        (Array.isArray(loadedPayload.strengthRom) ? loadedPayload.strengthRom : []).map(
          (row) => [row.joint, row],
        ),
      );
      const strengthRom = STRENGTH_ROM_JOINTS.map((joint) => ({
        ...emptyStrengthRomRows().find((r) => r.joint === joint),
        ...(byJoint[joint] || {}),
        joint,
      }));

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        evalDate: data.evalDate || loadedPayload.evalDate || data.visitDate || today(),
        commonHeader: normalizePtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        strengthRom,
        shortTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "shortTermGoals",
          legacyPrefix: "shortTermGoal",
          kind: "st",
          minCount: 1,
        }),
        longTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "longTermGoals",
          legacyPrefix: "longTermGoal",
          kind: "lt",
          minCount: 1,
        }),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      PT_EVAL_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : [];
      });

      merged.livesWith = coerceExclusiveChoice(
        loadedPayload.livesWith ?? base.livesWith,
      );
      merged.residenceType = coerceExclusiveChoice(
        loadedPayload.residenceType ?? base.residenceType,
      );

      if (merged.painLevel != null && merged.painLevel !== "") {
        merged.painLevel = Number(merged.painLevel);
        if (Number.isNaN(merged.painLevel)) merged.painLevel = null;
      }

      for (let n = 1; n <= 12; n += 1) {
        delete merged[`shortTermGoal${n}`];
        delete merged[`longTermGoal${n}`];
      }

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolvePtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Physical Therapy Evaluation Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            evalDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyPtEvalPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getPtEvalNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolvePtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => {
      let next = toggleInList(prev[key], item);
      if (key === "barriersToProgress") {
        if (item === "None" && next.includes("None")) {
          next = ["None"];
        } else if (item !== "None") {
          next = next.filter((x) => x !== "None");
        }
      }
      return { ...prev, [key]: next };
    });
  };

  const updateStrengthRom = (joint, patch) => {
    setValues((prev) => ({
      ...prev,
      strengthRom: (prev.strengthRom || []).map((row) =>
        row.joint === joint ? { ...row, ...patch } : row,
      ),
    }));
  };

  const updateTextGoal = (listKey, goalId, text) => {
    setValues((prev) => ({
      ...prev,
      [listKey]: (prev[listKey] || []).map((row) =>
        row.id === goalId ? { ...row, text } : row,
      ),
    }));
  };

  const addTextGoal = (listKey, kind) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          [...current, emptyTextGoalRow(current.length + 1, kind)],
          kind,
        ),
      };
    });
  };

  const removeTextGoal = (listKey, kind, goalId) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      if (current.length <= 1) return prev;
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          current.filter((row) => row.id !== goalId),
          kind,
        ),
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        PT_FORM_EDITOR_ROLES,
        "View only — only PT/PTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      evalDate,
      ...ptFields
    } = values;

    const nestedPayload = withNormalizedPtCommonHeader({
      ...ptFields,
      onsetDate: values.onsetDate ? formatDateForInput(values.onsetDate) : "",
      surgicalDate: values.surgicalDate
        ? formatDateForInput(values.surgicalDate)
        : "",
      anticipatedDischargeDate: values.anticipatedDischargeDate
        ? formatDateForInput(values.anticipatedDischargeDate)
        : "",
      strengthRom: (values.strengthRom || []).map((row) => ({
        joint: row.joint,
        mmtRight: row.mmtRight || "",
        mmtLeft: row.mmtLeft || "",
        romRight: row.romRight || "",
        romLeft: row.romLeft || "",
      })),
      shortTermGoals: serializeTextGoals(values.shortTermGoals),
      longTermGoals: serializeTextGoals(values.longTermGoals),
    });

    PT_EVAL_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    for (let n = 1; n <= 12; n += 1) {
      delete nestedPayload[`shortTermGoal${n}`];
      delete nestedPayload[`longTermGoal${n}`];
    }

    const payload = {
      flag,
      evalDate: evalDate ? formatDateForInput(evalDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updatePtEvalNoteForm,
        filledByUserId,
        scheduleDate:
          formatDateForInput(values.evalDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showLivesOther = values.livesWith === "Other";
  const showResidenceOther = values.residenceType === "Other";
  const showInterventionOther = (values.interventions || []).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Physical Therapy Evaluation Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health clinical documentation for skilled PT evaluation.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Evaluation Date"
          dateValue={displayDateUS(values.evalDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Evaluation Date
                </div>
                <InputDatePicker
                  name="evalDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.evalDate) || ""}
                  onChange={(event) => setValue("evalDate", event.target.value)}
                  max={EVAL_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        {/* Fixed sections 1–6: Patient / Company / Insurance / Physician / Diagnosis / Medication */}
        <PtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <div className="rounded-md border border-slate-200 bg-slate-50/40 px-3 py-2.5">
          <SubHeading>Diagnosis Dates</SubHeading>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Onset Date">
              <DateFieldInput
                name="onsetDate"
                value={values.onsetDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("onsetDate", v)}
              />
            </Field>
            <Field label="Surgical Date">
              <DateFieldInput
                name="surgicalDate"
                value={values.surgicalDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("surgicalDate", v)}
              />
            </Field>
          </div>
        </div>

        {/* 7. Living Situation */}
        <Section icon={HomeIcon} title="7. Living Situation">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <SubHeading>Lived With</SubHeading>
              <ExclusiveChoiceGrid
                name="livesWith"
                options={LIVES_WITH_OPTIONS}
                value={values.livesWith || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("livesWith", opt)}
                columns={2}
              />
              {showLivesOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.livesWithOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify…"
                  onChange={(e) => setValue("livesWithOther", e.target.value)}
                />
              ) : null}
            </div>
            <div>
              <SubHeading>Residence Type</SubHeading>
              <ExclusiveChoiceGrid
                name="residenceType"
                options={RESIDENCE_TYPE_OPTIONS}
                value={values.residenceType || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("residenceType", opt)}
                columns={2}
              />
              {showResidenceOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.residenceTypeOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify…"
                  onChange={(e) =>
                    setValue("residenceTypeOther", e.target.value)
                  }
                />
              ) : null}
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50/50 p-2.5 space-y-2">
              <Field label="Stairs">
                <SelectField
                  value={values.stairs}
                  options={STAIRS_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("stairs", v)}
                />
              </Field>
              <div>
                <SubHeading>Railing</SubHeading>
                <ExclusiveChoiceGrid
                  name="railing"
                  options={YES_NO_OPTIONS}
                  value={values.railing || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("railing", opt)}
                  columns={2}
                />
              </div>
              <Field label="Entrance">
                <SelectField
                  value={values.entrance}
                  options={ENTRANCE_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("entrance", v)}
                />
              </Field>
            </div>
          </div>
        </Section>

        {/* 8. Subjective / History */}
        <Section icon={MedicalNotesIcon} title="8. Subjective / History">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Prior Level of Function (PLOF)">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.plof || ""}
                disabled={!canEdit}
                placeholder="Describe patient's prior functional status…"
                onChange={(e) => setValue("plof", e.target.value)}
              />
            </Field>
            <Field label="Current Level of Function / Limitations">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.currentLevelOfFunction || ""}
                disabled={!canEdit}
                placeholder="Describe current function and limitations…"
                onChange={(e) =>
                  setValue("currentLevelOfFunction", e.target.value)
                }
              />
            </Field>
            <Field label="Chief Complaint / Patient Goal">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.chiefComplaint || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("chiefComplaint", e.target.value)}
              />
            </Field>
            <Field label="History of Present Illness">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.historyPresentIllness || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("historyPresentIllness", e.target.value)
                }
              />
            </Field>
            <Field label="Medication History">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.medicationHistory || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("medicationHistory", e.target.value)}
              />
            </Field>
            <Field label="Allergies">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.allergiesNarrative || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("allergiesNarrative", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Current Medications">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.currentMedicationsNarrative || ""}
                disabled={!canEdit}
                placeholder="List current medications (in addition to Medication Info above)…"
                onChange={(e) =>
                  setValue("currentMedicationsNarrative", e.target.value)
                }
              />
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Relieving Factors">
              <input
                type="text"
                className={fieldClass}
                value={values.relievingFactors || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("relievingFactors", e.target.value)}
              />
            </Field>
            <Field label="Aggravating Factors">
              <input
                type="text"
                className={fieldClass}
                value={values.aggravatingFactors || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("aggravatingFactors", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <PainScalePills
              label="Pain Assessment (0–10)"
              name="painLevel"
              value={values.painLevel}
              canEdit={canEdit}
              onChange={(v) => setValue("painLevel", v)}
            />
          </div>
        </Section>

        {/* 9. Objective Assessment */}
        <Section icon={RunningIcon} title="9. Objective Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="rounded-md border border-slate-200 p-2.5 space-y-2">
              <SubHeading>Vitals</SubHeading>
              {[
                ["vitalsBp", "BP", "120/80"],
                ["vitalsHr", "HR", "72"],
                ["vitalsSpo2", "SpO2 %", "98"],
                ["vitalsRr", "RR", "18"],
                ["vitalsTemp", "Temp", "98.6°F"],
              ].map(([key, label, ph]) => (
                <Field key={key} label={label}>
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values[key] || ""}
                    disabled={!canEdit}
                    placeholder={ph}
                    onChange={(e) => setValue(key, e.target.value)}
                  />
                </Field>
              ))}
            </div>

            <div className="lg:col-span-2 rounded-md border border-slate-200 overflow-hidden">
              <div className="px-2.5 py-1.5 border-b border-slate-200 bg-slate-50">
                <SubHeading>Strength &amp; ROM Assessment</SubHeading>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-[11px]">
                  <thead>
                    <tr className="bg-slate-50/80 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-1.5">Joint Action</th>
                      <th className="px-2 py-1.5 text-center" colSpan={2}>
                        MMT (0–5)
                      </th>
                      <th className="px-2 py-1.5 text-center" colSpan={2}>
                        ROM (°)
                      </th>
                    </tr>
                    <tr className="bg-slate-50/50 text-[9px] font-semibold text-slate-400">
                      <th />
                      <th className="px-2 py-1 text-center">Right</th>
                      <th className="px-2 py-1 text-center">Left</th>
                      <th className="px-2 py-1 text-center">Right</th>
                      <th className="px-2 py-1 text-center">Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(values.strengthRom || []).map((row) => (
                      <tr key={row.joint} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-medium text-slate-700 whitespace-nowrap">
                          {row.joint}
                        </td>
                        {["mmtRight", "mmtLeft", "romRight", "romLeft"].map(
                          (field) => (
                            <td key={field} className="px-1 py-0.5">
                              <input
                                type="text"
                                className={`${fieldClass} text-center tabular-nums ${
                                  looksAbnormal(row[field])
                                    ? "text-red-600 border-red-200"
                                    : ""
                                }`}
                                value={row[field] || ""}
                                disabled={!canEdit}
                                onChange={(e) =>
                                  updateStrengthRom(row.joint, {
                                    [field]: e.target.value,
                                  })
                                }
                              />
                            </td>
                          ),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-md border border-slate-200 p-2.5">
            <SubHeading>Functional Mobility</SubHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <Field label="Bed Mobility">
                <SelectField
                  value={values.bedMobility}
                  options={BED_MOBILITY_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("bedMobility", v)}
                />
              </Field>
              <Field label="Transfers">
                <SelectField
                  value={values.transfers}
                  options={TRANSFER_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("transfers", v)}
                />
              </Field>
              <Field label="Ambulation">
                <SelectField
                  value={values.ambulation}
                  options={AMBULATION_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("ambulation", v)}
                />
              </Field>
              <Field label="Distance (ft)">
                <input
                  type="text"
                  className={`${fieldClass} tabular-nums`}
                  value={values.ambulationDistanceFt || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("ambulationDistanceFt", e.target.value)
                  }
                />
              </Field>
            </div>
            <div className="mt-2">
              <Field label="Assistive Device">
                <SelectField
                  value={values.assistiveDevice}
                  options={ASSISTIVE_DEVICE_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("assistiveDevice", v)}
                />
              </Field>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 p-2.5 space-y-2">
              <SubHeading>Observation</SubHeading>
              <Field label="Posture">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.posture || ""}
                  disabled={!canEdit}
                  placeholder="Describe posture…"
                  onChange={(e) => setValue("posture", e.target.value)}
                />
              </Field>
              <div>
                <SubHeading>Gait Aid</SubHeading>
                <ExclusiveChoiceGrid
                  name="gaitAid"
                  options={GAIT_AID_OPTIONS}
                  value={values.gaitAid || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("gaitAid", opt)}
                  columns={3}
                />
              </div>
              <div>
                <SubHeading>Skin Integrity</SubHeading>
                <ExclusiveChoiceGrid
                  name="skinIntegrity"
                  options={SKIN_INTEGRITY_OPTIONS}
                  value={values.skinIntegrity || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("skinIntegrity", opt)}
                  columns={2}
                />
              </div>
            </div>

            <div className="rounded-md border border-slate-200 p-2.5 space-y-2">
              <SubHeading>Balance</SubHeading>
              <ExclusiveChoiceGrid
                name="balanceStatus"
                options={BALANCE_STATUS_OPTIONS}
                value={values.balanceStatus || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("balanceStatus", opt)}
                columns={3}
              />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Tinetti Score">
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      className={`${fieldClass} tabular-nums`}
                      value={values.tinettiScore || ""}
                      disabled={!canEdit}
                      onChange={(e) => setValue("tinettiScore", e.target.value)}
                    />
                    <span className="text-[11px] text-slate-400 shrink-0">
                      /28
                    </span>
                  </div>
                </Field>
                <Field label="Berg Balance">
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      className={`${fieldClass} tabular-nums`}
                      value={values.bergBalance || ""}
                      disabled={!canEdit}
                      onChange={(e) => setValue("bergBalance", e.target.value)}
                    />
                    <span className="text-[11px] text-slate-400 shrink-0">
                      /56
                    </span>
                  </div>
                </Field>
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 p-2.5 space-y-2">
              <SubHeading>Special Tests</SubHeading>
              <div className="grid grid-cols-2 gap-2">
                <Field label="TUG (sec)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.tugSec || ""}
                    disabled={!canEdit}
                    onChange={(e) => setValue("tugSec", e.target.value)}
                  />
                </Field>
                <Field label="10 Meter Walk (m/s)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.tenMeterWalk || ""}
                    disabled={!canEdit}
                    onChange={(e) => setValue("tenMeterWalk", e.target.value)}
                  />
                </Field>
                <Field label="5x Sit to Stand (sec)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.fiveXSitToStand || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("fiveXSitToStand", e.target.value)
                    }
                  />
                </Field>
                <Field label="Other">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.specialTestOther || ""}
                    disabled={!canEdit}
                    placeholder="Specify…"
                    onChange={(e) =>
                      setValue("specialTestOther", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 p-2.5 space-y-2">
              <div className="flex items-center gap-1.5">
                <AlertCircleIcon size={12} className="text-red-500" />
                <SubHeading>Fall Risk</SubHeading>
              </div>
              <ExclusiveChoiceGrid
                name="fallRisk"
                options={FALL_RISK_OPTIONS}
                value={values.fallRisk || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("fallRisk", opt)}
                columns={3}
              />
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.fallRiskComments || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("fallRiskComments", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </Section>

        {/* 10. Assessment */}
        <Section icon={FileCheckIcon} title="10. Assessment">
          <Field label="Clinical Summary">
            <textarea
              rows={4}
              className={textareaClass}
              value={values.clinicalSummary || ""}
              disabled={!canEdit}
              placeholder="Provide a concise summary of clinical findings, functional limitations, and medical necessity for skilled intervention…"
              onChange={(e) => setValue("clinicalSummary", e.target.value)}
            />
          </Field>
          <div className="mt-3">
            <SubHeading>Rehabilitation Potential</SubHeading>
            <ExclusiveChoiceGrid
              name="rehabPotential"
              options={REHAB_POTENTIAL_OPTIONS}
              value={values.rehabPotential || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("rehabPotential", opt)}
              columns={4}
            />
          </div>
          <div className="mt-3">
            <SubHeading>Barriers to Progress</SubHeading>
            <CheckboxGrid
              options={BARRIERS_OPTIONS}
              values={values.barriersToProgress}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("barriersToProgress", opt)}
              columns={4}
            />
          </div>
        </Section>

        {/* 11. Plan of Care */}
        <Section icon={TargetIcon} title="11. Plan of Care">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <SubHeading>Skilled PT Indicated?</SubHeading>
            <div className="min-w-[12rem]">
              <ExclusiveChoiceGrid
                name="skilledPtIndicated"
                options={YES_NO_OPTIONS}
                value={values.skilledPtIndicated || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("skilledPtIndicated", opt)}
                columns={2}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-3">
              <Field label="Frequency">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.frequency || ""}
                  disabled={!canEdit}
                  placeholder="e.g., 2x per week"
                  onChange={(e) => setValue("frequency", e.target.value)}
                />
              </Field>
              <Field label="Anticipated Discharge Date">
                <DateFieldInput
                  name="anticipatedDischargeDate"
                  value={values.anticipatedDischargeDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("anticipatedDischargeDate", v)}
                />
              </Field>
              <div>
                <SubHeading>Interventions</SubHeading>
                <CheckboxGrid
                  options={PT_INTERVENTION_OPTIONS}
                  values={values.interventions}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("interventions", opt)}
                  columns={1}
                />
                {showInterventionOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.interventionOther || ""}
                    disabled={!canEdit}
                    placeholder="Other intervention…"
                    onChange={(e) =>
                      setValue("interventionOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
            </div>
            <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-3 space-y-3">
              <SubHeading>Measurable Goals</SubHeading>
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Short Term Goals (2 Weeks)
                </p>
                {(values.shortTermGoals || []).map((row, idx) => (
                  <div key={row.id} className="flex gap-2 min-w-0">
                    <span
                      className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-bold text-indigo-700"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    <textarea
                      rows={2}
                      className={`${textareaClass} min-w-0 flex-1`}
                      value={row.text || ""}
                      disabled={!canEdit}
                      placeholder="e.g., Pt will ambulate 50 ft with FWW…"
                      aria-label={`Short-term goal ${idx + 1}`}
                      onChange={(e) =>
                        updateTextGoal("shortTermGoals", row.id, e.target.value)
                      }
                    />
                    {canEdit ? (
                      <button
                        type="button"
                        disabled={(values.shortTermGoals || []).length <= 1}
                        onClick={() =>
                          removeTextGoal("shortTermGoals", "st", row.id)
                        }
                        className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                        aria-label={`Remove short-term goal ${idx + 1}`}
                      >
                        <DeleteIcon size={14} />
                      </button>
                    ) : null}
                  </div>
                ))}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => addTextGoal("shortTermGoals", "st")}
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                  >
                    <PlusIcon size={13} />
                    Add Goal
                  </button>
                ) : null}
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Long Term Goals (4 Weeks)
                </p>
                {(values.longTermGoals || []).map((row, idx) => (
                  <div key={row.id} className="flex gap-2 min-w-0">
                    <span
                      className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    <textarea
                      rows={2}
                      className={`${textareaClass} min-w-0 flex-1`}
                      value={row.text || ""}
                      disabled={!canEdit}
                      placeholder="e.g., Pt will perform independent transfers…"
                      aria-label={`Long-term goal ${idx + 1}`}
                      onChange={(e) =>
                        updateTextGoal("longTermGoals", row.id, e.target.value)
                      }
                    />
                    {canEdit ? (
                      <button
                        type="button"
                        disabled={(values.longTermGoals || []).length <= 1}
                        onClick={() =>
                          removeTextGoal("longTermGoals", "lt", row.id)
                        }
                        className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                        aria-label={`Remove long-term goal ${idx + 1}`}
                      >
                        <DeleteIcon size={14} />
                      </button>
                    ) : null}
                  </div>
                ))}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => addTextGoal("longTermGoals", "lt")}
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                  >
                    <PlusIcon size={13} />
                    Add Goal
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="PT / PTA Authentication"
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

PtEvalNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default PtEvalNoteForm;
